import { AnimatePresence, motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { VARIANTS } from '@/lib/motion';

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    variant?: 'danger' | 'default';
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
}

export function ConfirmDialog({
    open,
    title,
    description,
    confirmLabel = 'Bestätigen',
    cancelLabel = 'Abbrechen',
    variant = 'default',
    onConfirm,
    onCancel,
    isLoading,
}: ConfirmDialogProps) {
    return (
        <AnimatePresence>
            {open && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        variants={VARIANTS.backdrop}
                        initial="hidden"
                        animate="visible"
                        exit="exit"
                        className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm"
                        onClick={onCancel}
                    />
                    {/* Dialog */}
                    <motion.div
                        variants={VARIANTS.scaleIn}
                        initial="hidden"
                        animate="visible"
                        exit="hidden"
                        className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-4"
                    >
                        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-2xl p-6">
                            {variant === 'danger' && (
                                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-red-100 dark:bg-red-900/30">
                                    <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
                                </div>
                            )}
                            <h3 className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{title}</h3>
                            {description && (
                                <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
                                    {description}
                                </p>
                            )}
                            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                <Button variant="outline" onClick={onCancel} disabled={isLoading}>
                                    {cancelLabel}
                                </Button>
                                <Button
                                    variant={variant === 'danger' ? 'danger' : 'primary'}
                                    onClick={onConfirm}
                                    loading={isLoading}
                                >
                                    {confirmLabel}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
}
